type Corner = 'tl' | 'tr' | 'bl' | 'br';

interface BrandWatermarkProps {
  variant: 'mark' | 'arrows';
  tone: 'white' | 'forest';
  corner: Corner;
  /** Tilt in degrees (D9: -8 to -12). */
  rotate?: number;
  size?: string;
  opacityClass?: string;
  /** Slow scroll-linked drift (motion.css `.watermark-drift`). */
  drift?: boolean;
  className?: string;
}

const CORNER: Record<Corner, string> = {
  tl: '-left-24 -top-24',
  tr: '-right-24 -top-24',
  bl: '-left-24 -bottom-24',
  br: '-right-24 -bottom-24',
};

/** Published files (design-assets/build-brand-graphics.sh) and their intrinsic size. */
function asset(variant: BrandWatermarkProps['variant'], tone: BrandWatermarkProps['tone']) {
  if (variant === 'mark') {
    const src = `/images/brand/mark-${tone}-1280.webp`;
    return { src, srcSet: `${src} 1280w`, width: 1280, height: 462 };
  }
  if (tone === 'white') {
    return {
      src: '/images/brand/arrows-white-640.webp',
      srcSet: '/images/brand/arrows-white-640.webp 640w, /images/brand/arrows-white-1280.webp 1280w',
      width: 1280,
      height: 1311,
    };
  }
  // Forest-toned arrows use the full-color cluster: at watermark opacity it reads as green.
  return {
    src: '/images/brand/arrows-color-320.webp',
    srcSet: '/images/brand/arrows-color-320.webp 320w, /images/brand/arrows-color-640.webp 640w',
    width: 640,
    height: 655,
  };
}

/**
 * The freshSTART logo as a faded, tilted, oversized watermark that bleeds off one corner
 * (PRP-15, D9). Decorative only: hidden from assistive tech, lazy, never interactive.
 * The parent must be `relative overflow-hidden`, and content above it `relative z-10`.
 * Tilt uses the CSS `rotate` property so the scroll drift (`translate`) composes with it.
 */
export function BrandWatermark({
  variant,
  tone,
  corner,
  rotate = -10,
  size = 'w-[38rem] sm:w-[52rem]',
  opacityClass = 'opacity-[0.07]',
  drift = true,
  className = '',
}: BrandWatermarkProps) {
  const a = asset(variant, tone);
  return (
    <img
      src={a.src}
      srcSet={a.srcSet}
      sizes="(min-width: 640px) 52rem, 38rem"
      width={a.width}
      height={a.height}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      data-testid="brand-watermark"
      style={{ rotate: `${rotate}deg` }}
      className={`pointer-events-none select-none absolute z-0 max-w-none h-auto ${CORNER[corner]} ${size} ${opacityClass} [mask-image:radial-gradient(closest-side,black_60%,transparent)] ${drift ? 'watermark-drift' : ''} ${className}`}
    />
  );
}
